import { isNull, has, compose } from 'lodash/fp';
import { AppClockServiceProps, createAction, TimeService } from '@mews/framework';
import {
    ApplicationState,
    CommanderThunkAction,
    isHoldingState,
    isInquiredState,
    WithScope,
} from '@mews-commander/types';
import { ServiceOrderState } from '@mews-commander/data-access';
import { validateSingle } from '@mews-commander/legacy-form';
import {
    getSendConfirmationEmailByDefault,
    getServiceArrivalTime,
} from '@mews-commander/legacy-entities/src/ducks/services';
import { getCurrentDateTimeLocalizationInfo } from '@mews-commander/core/legacy-globalization';
import {
    getOptionConstraints,
    getServiceId,
    getDefaultReleaseDateString,
    getConfirmationEmailReceivers,
    getReservationsMinimalArrivalUtc,
    getReleaseTime,
    getReleaseDate,
    getTouchedOptions,
    isDefinite,
} from '../selectors';
import MailReceivers from '../enums/MailReceivers';
import { OPTION_VALUE_CHANGED, OPTION_VALUE_TOUCHED } from './types';

export const createChangeOptionValueAction = (key: $TSFixMe) => (value: $TSFixMe, scope: $TSFixMe) =>
    changeOptionValue({ key, value }, scope);

const validateOptionValue = (
    { key, value }: $TSFixMe,
    state: ApplicationState,
    { scope, appClockService }: WithScope & AppClockServiceProps
) => {
    let reservationMinArrivalUtc;

    if (!isDefinite(state, { scope })) {
        const serviceId = getServiceId(state, { scope });
        // @ts-expect-error
        reservationMinArrivalUtc = getReservationsMinimalArrivalUtc(state, { id: serviceId, scope, appClockService });
    }

    // @ts-expect-error $TSFixMeError: Type 'any' cannot be used as an index type.
    const { [key]: constraints } = getOptionConstraints(state, { scope, reservationMinArrivalUtc, key });
    if (constraints) {
        return validateSingle(value, { ...constraints }) || null;
    }

    return null;
};

export const changeOptionValue =
    ({ key, value }: $TSFixMe, scope: string): CommanderThunkAction =>
    (dispatch, getState, { appClockService }) => {
        const state = getState();
        const error = validateOptionValue({ key, value }, state, { scope, appClockService });

        dispatch(createAction(OPTION_VALUE_CHANGED, { key, value, error }, { scope }));

        if (error) {
            dispatch(createAction(OPTION_VALUE_TOUCHED, { key }, { scope }));
        }

        if (!isDefinite(state, { scope }) && !error) {
            const updatedState = getState();
            const pairKey = key === 'releaseDate' ? 'releaseTime' : 'releaseDate';
            const pairValue =
                key === 'releaseDate'
                    ? getReleaseTime(updatedState, { scope })
                    : getReleaseDate(updatedState, { scope });
            const pairError = validateOptionValue({ key: pairKey, value: pairValue }, updatedState, {
                scope,
                appClockService,
            });

            dispatch(
                createAction(OPTION_VALUE_CHANGED, { key: pairKey, value: pairValue, error: pairError }, { scope })
            );

            if (pairError && !compose(has(pairKey), getTouchedOptions)(updatedState, { scope })) {
                dispatch(createAction(OPTION_VALUE_TOUCHED, { key: pairKey }, { scope }));
            }
        }
    };

export const touchOptionValue = (key: $TSFixMe, scope: $TSFixMe) =>
    createAction(OPTION_VALUE_TOUCHED, { key }, { scope });

export const changeOrigin = createChangeOptionValueAction('origin');
export const changeCustomerId = createChangeOptionValueAction('customerId');
export const changePartnerCompanyId = createChangeOptionValueAction('partnerCompanyId');
export const changeTravelAgencyId = createChangeOptionValueAction('travelAgencyId');
export const changeReservationGroupId = createChangeOptionValueAction('reservationGroupId');
export const changeReleaseDate = createChangeOptionValueAction('releaseDate');
export const changeReleaseTime = createChangeOptionValueAction('releaseTime');

export const changeBookerId = (value: $TSFixMe, scope: $TSFixMe) => (dispatch: $TSFixMe, getState: $TSFixMe) => {
    if (isNull(value)) {
        const state = getState();
        const currentValue = getConfirmationEmailReceivers(state, { scope });
        const serviceId = getServiceId(state, { scope });
        if (currentValue === MailReceivers.Booker.value) {
            const sendConfirmation = getSendConfirmationEmailByDefault(state, { id: serviceId });
            const confirmation = sendConfirmation ? MailReceivers.CustomerAndBooker : MailReceivers.None;
            dispatch(changeConfirmationEmailReceiversValue(confirmation.value, scope));
        }
    }

    dispatch(changeOptionValue({ key: 'bookerId', value }, scope));
};

export const changeStateValue =
    (value: ServiceOrderState, scope: string): CommanderThunkAction =>
    (dispatch, getState, { appClockService }) => {
        dispatch(changeOptionValue({ key: 'state', value }, scope));

        if (isHoldingState(value) || isInquiredState(value)) {
            // set defaults
            const state = getState();
            const serviceId = getServiceId(state, { scope });
            const serviceArrivalTime = getServiceArrivalTime(state, { id: serviceId });

            const defaultReleaseTimeString =
                serviceArrivalTime &&
                TimeService.toShortString(
                    TimeService.parse(serviceArrivalTime),
                    getCurrentDateTimeLocalizationInfo(state)
                );

            // @ts-expect-error
            const defaultReleaseDateString = getDefaultReleaseDateString(state, { scope, appClockService });

            dispatch(changeOptionValue({ key: 'releaseDate', value: defaultReleaseDateString }, scope));
            dispatch(changeOptionValue({ key: 'releaseTime', value: defaultReleaseTimeString }, scope));
            dispatch(
                changeOptionValue(
                    { key: 'confirmationEmailReceivers', value: MailReceivers.CustomerAndBooker.value },
                    scope
                )
            );
        } else {
            dispatch(changeOptionValue({ key: 'releaseDate', value: null }, scope));
            dispatch(changeOptionValue({ key: 'releaseTime', value: null }, scope));
            dispatch(changeOptionValue({ key: 'confirmationEmailReceivers', value: MailReceivers.None.value }, scope));
        }
    };

export const changeConfirmationEmailReceiversValue = (nextValue: $TSFixMe, scope: $TSFixMe) => (dispatch: $TSFixMe) => {
    if (nextValue === MailReceivers.None.value) {
        // clear values
        dispatch(changeOptionValue({ key: 'confirmationLanguageCode', value: null }, scope));
    }

    dispatch(changeOptionValue({ key: 'confirmationEmailReceivers', value: nextValue }, scope));
};
